import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { firstValueFrom, timeout } from 'rxjs';
import { ApplicationForm } from '../../auth/routes/models/application-form.type';
import { BrReg } from '../../auth/routes/models/brReg.type';
import { UserProfilePatchRequest } from '../../user-profile/models/user-profile-patch-request.type';
import { PhoneCodeResponse } from '../../user-profile/models/phone-code-response.type';
import { UserProfileResponse } from '../../user-profile/models/user-profile-response.type';
import { SelfApplication } from '../../user-profile/models/self-application.type';
import {
  SendVerificationEmailRequest,
  SendVerificationSmsRequest,
  VerificationResult,
  VerifyEmailRequest,
  VerifySmsRequest,
} from '../../user-profile/models/verification.type';
import { environment } from '../../../../environments/environment';
import { EmployerAccessEmployer } from '../models/employer.type';
import {
  DeductionListPeriodPatchRequest,
  DeductionListPeriodsResponse,
  DeductionListUpload,
  SendUploadsResponse,
} from '../../../shared/components/periodfilter/models/deduction-list-period';

export type DeductionListRowDtoRequest = {
  deductionListRowId: string;
  deductionListPeriodId: string;
  employee: {
    nationalIdentityNumber: string;
    firstName: string;
    lastName: string;
    employmentPercentage: number;
  };
  employmentId: string;
  sum: number;
  membershipDueMaxAmount: number;
  status: number;
  comment: string;
  dataSource: number;
  deductions: Array<{ deductionType: string | number; amount: number }>;
};


@Injectable({
  providedIn: 'root',
})
export class EmployerService {
  private baseUrl = environment.apis.employerApi;
  private readonly requestTimeoutMs = 25000;

  constructor(private http: HttpClient) {}


  async getSelf(): Promise<UserProfileResponse> {
    const requestUrl = `${this.baseUrl}/employerservices/self`;

    return firstValueFrom(this.http.get<UserProfileResponse>(requestUrl).pipe(timeout(this.requestTimeoutMs)));
  }

  async getSelfApplications(): Promise<SelfApplication[]> {
    const requestUrl = `${this.baseUrl}/employerservices/self/applications`;
    return firstValueFrom(this.http.get<SelfApplication[]>(requestUrl).pipe(timeout(this.requestTimeoutMs)));
  }

  async patchSelf(payload: UserProfilePatchRequest): Promise<UserProfileResponse> {
    const requestUrl = `${this.baseUrl}/employerservices/self`;

    return firstValueFrom(this.http.put<UserProfileResponse>(requestUrl, payload).pipe(timeout(this.requestTimeoutMs)));
  }

  async sendVerificationEmail(payload: SendVerificationEmailRequest): Promise<VerificationResult> {
    const requestUrl = `${this.baseUrl}/employerservices/self/send-verification-email`;
    return this.postVerificationResult(requestUrl, payload);
  }

  async sendVerificationSms(payload: SendVerificationSmsRequest): Promise<VerificationResult> {
    const requestUrl = `${this.baseUrl}/employerservices/self/send-verification-sms`;
    return this.postVerificationResult(requestUrl, payload);
  }

  async verifyEmail(payload: VerifyEmailRequest): Promise<VerificationResult> {
    const requestUrl = `${this.baseUrl}/employerservices/self/verify-email`;
    return this.postVerificationResult(requestUrl, payload);
  }

  async verifySms(payload: VerifySmsRequest): Promise<VerificationResult> {
    const requestUrl = `${this.baseUrl}/employerservices/self/verify-sms`;
    return this.postVerificationResult(requestUrl, payload);
  }

  async applyForEmployer(applications: ApplicationForm[]): Promise<void> {
    const requestUrl = `${this.baseUrl}/employerservices/apply`;
    await firstValueFrom(this.http.post(requestUrl, applications, { responseType: 'text' }).pipe(timeout(this.requestTimeoutMs)));
  }

  async EmployerAccess(): Promise<EmployerAccessEmployer[]> {
    const requestUrl = `${this.baseUrl}/employerservices/employeraccess`;

    const employers = await firstValueFrom(
      this.http
        .get<Array<EmployerAccessEmployer & { accountId?: string }>>(requestUrl)
        .pipe(timeout(this.requestTimeoutMs))
    );

    return employers.map((employer) => ({
      ...employer,
      employerId: employer.employerId ?? employer.accountId ?? '',
    }));
  }

  async patchEmployerDeductionContactEmail(orgId: string, deductionContactEmail: string | null): Promise<EmployerAccessEmployer | null> {
    const requestUrl = `${this.baseUrl}/employerservices/Employers/${orgId}`;

    return firstValueFrom(
      this.http.patch<EmployerAccessEmployer | null>(requestUrl, { deductionContactEmail }).pipe(timeout(this.requestTimeoutMs))
    );
  }

  async getPhoneCodes(): Promise<PhoneCodeResponse[]> {
    const requestUrl = `${this.baseUrl}/organizationInfo/phonecodes`;
    return firstValueFrom(this.http.get<PhoneCodeResponse[]>(requestUrl).pipe(timeout(this.requestTimeoutMs)));
  }

  async getOrganizationFromBrReg(orgNumber: string): Promise<BrReg> {
    const requestUrl = `${this.baseUrl}/employerservices/brReg?orgNumber=${orgNumber}`;

    return firstValueFrom(this.http.get<BrReg>(requestUrl).pipe(timeout(this.requestTimeoutMs)));
  }

  async getDeductionListsByOrganization(organizationNumber: string): Promise<DeductionListPeriodsResponse> {
    const requestUrl = `${this.baseUrl}/employerservices/${organizationNumber}/deductionlistperiods`;

    const payload = await firstValueFrom(this.http.get<unknown>(requestUrl).pipe(timeout(this.requestTimeoutMs)));

    if (Array.isArray(payload)) {
      return { deductionListPeriods: this.normalizeDeductionListPeriods(payload) };
    }

    if (payload && typeof payload === 'object') {
      const candidate = payload as { deductionListPeriods?: unknown; periodYear?: unknown; periodMonth?: unknown };
      if (Array.isArray(candidate.deductionListPeriods)) {
        return { deductionListPeriods: this.normalizeDeductionListPeriods(candidate.deductionListPeriods) };
      }

      // Some environments may return one period object directly.
      if (candidate.periodYear !== undefined && candidate.periodMonth !== undefined) {
        return { deductionListPeriods: this.normalizeDeductionListPeriods([candidate]) };
      }
    }

    return { deductionListPeriods: [] };
  }

  private normalizeDeductionListPeriods(periods: unknown[]): any[] {
    return periods.map((period) => {
      if (!period || typeof period !== 'object') {
        return period;
      }

      const value = period as {
        deductionListPeriodId?: unknown;
        DeductionListPeriodId?: unknown;
        employerPeriodDeductionListReviewId?: unknown;
        EmployerPeriodDeductionListReviewId?: unknown;
      };

      return {
        ...value,
        deductionListPeriodId:
          value.deductionListPeriodId ??
          value.DeductionListPeriodId ??
          value.employerPeriodDeductionListReviewId ??
          value.EmployerPeriodDeductionListReviewId ??
          '',
      };
    });
  }

  async getUploadPeriodByDeductionListPeriodId(deductionListPeriodId: string): Promise<DeductionListUpload[]> {
    const requestUrl = `${this.baseUrl}/employerservices/${deductionListPeriodId}/uploadperiod`;

    return firstValueFrom(this.http.get<DeductionListUpload[]>(requestUrl).pipe(timeout(this.requestTimeoutMs)));
  }

  async getRowsByDeductionListPeriodId(deductionListPeriodId: string): Promise<unknown> {
    const requestUrl = `${this.baseUrl}/employerservices/${deductionListPeriodId}/rows`;

    return firstValueFrom(this.http.get<unknown>(requestUrl).pipe(timeout(this.requestTimeoutMs)));
  }

  async patchDeductionListPeriodById(
    deductionListPeriodId: string,
    payload: DeductionListPeriodPatchRequest
  ): Promise<unknown> {
    const requestUrl = `${this.baseUrl}/employerservices/deductionlistperiod/${deductionListPeriodId}`;

    return firstValueFrom(this.http.patch<unknown>(requestUrl, payload).pipe(timeout(this.requestTimeoutMs)));
  }

  async uploadDeductionListPeriodFile(deductionListPeriodId: string, file: File): Promise<DeductionListUpload> {
    const requestUrl = `${this.baseUrl}/employerservices/deductionlistperiod/${deductionListPeriodId}/upload`;
    const formData = new FormData();
    formData.append('file', file, file.name);

    return firstValueFrom(this.http.post<DeductionListUpload>(requestUrl, formData).pipe(timeout(this.requestTimeoutMs)));
  }

  async deleteDeductionListUploadById(deductionListUploadId: string): Promise<void> {
    const requestUrl = `${this.baseUrl}/employerservices/deductionlistuploads/${deductionListUploadId}`;

    await firstValueFrom(this.http.delete<void>(requestUrl).pipe(timeout(this.requestTimeoutMs)));
  }

  async sendUploadsForDeductionListPeriod(deductionListPeriodId: string): Promise<SendUploadsResponse> {
    const requestUrl = `${this.baseUrl}/employerservices/deductionlistperiod/${deductionListPeriodId}/senduploads`;

    return firstValueFrom(this.http.put<SendUploadsResponse>(requestUrl, {}).pipe(timeout(this.requestTimeoutMs)));
  }

  async addRowToDeductionListPeriod(deductionListPeriodId: string, payload: DeductionListRowDtoRequest): Promise<unknown> {
    const requestUrl = `${this.baseUrl}/employerservices/deductionlistperiod/${deductionListPeriodId}/addrow`;
    return firstValueFrom(this.http.post<unknown>(requestUrl, payload).pipe(timeout(this.requestTimeoutMs)));
  }

  async updateDeductionListRow(deductionListRowId: string, payload: DeductionListRowDtoRequest): Promise<unknown> {
    const requestUrl = `${this.baseUrl}/employerservices/deductionlistrows/${deductionListRowId}`;
    return firstValueFrom(this.http.put<unknown>(requestUrl, payload).pipe(timeout(this.requestTimeoutMs)));
  }

  async deleteDeductionListRow(deductionListRowId: string): Promise<void> {
    const requestUrl = `${this.baseUrl}/employerservices/deductionlistrows/${deductionListRowId}`;
    await firstValueFrom(this.http.delete<void>(requestUrl).pipe(timeout(this.requestTimeoutMs)));
  }

  async sendRowsForDeductionListPeriod(deductionListPeriodId: string): Promise<SendUploadsResponse> {
    const requestUrl = `${this.baseUrl}/employerservices/deductionlistperiod/${deductionListPeriodId}/sendrows`;
    return firstValueFrom(this.http.put<SendUploadsResponse>(requestUrl, {}).pipe(timeout(this.requestTimeoutMs)));
  }

  async createRowsForDeductionListPeriod(deductionListPeriodId: string): Promise<unknown> {
    const requestUrl = `${this.baseUrl}/employerservices/deductionlistperiod/${deductionListPeriodId}/createrows`;
    return firstValueFrom(this.http.post<unknown>(requestUrl, {}).pipe(timeout(this.requestTimeoutMs)));
  }

  private async postVerificationResult<TPayload>(requestUrl: string, payload: TPayload): Promise<VerificationResult> {
    try {
      return await firstValueFrom(this.http.post<VerificationResult>(requestUrl, payload).pipe(timeout(this.requestTimeoutMs)));
    } catch (error) {
      const httpError = error as HttpErrorResponse;
      const backendPayload = httpError?.error;

      if (backendPayload && typeof backendPayload === 'object') {
        return backendPayload as VerificationResult;
      }

      throw error;
    }
  }
}
